import { useEffect, useRef, useReducer } from 'react'
import Dropzone, { IDropzoneProps } from 'react-dropzone-uploader'
import { Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { etlAPI } from '../../../shared/api/ETLAPI'
import { fsColors } from '../../../shared/constants'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setEtlFiles, setLatestEtlStatus } from '../../../store/actions'
import FsAlert, { FsAlertRef } from '../../common/atoms/Alert'
import { ReactComponent as UploadIcon } from '../../../assets/etl/cloud_icon.svg'
import FsContainer from '../common/Container'

const FsFixtures = ({ setIsLoading }: { setIsLoading: (isLoading: boolean) => void }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)
  const { latestEtlStatus } = useAppSelector((state) => state.currentEtl)
  const [, forceUpdate] = useReducer(x => x + 1, 0)

  useEffect(() => {
    if (!latestEtlStatus) {
      dispatch(setLatestEtlStatus())
    }
  }, [latestEtlStatus])

  const handleSubmit: IDropzoneProps['onSubmit'] = (_, allFiles) => {
    if (latestEtlStatus) {
      setIsLoading(true)
      etlAPI
        .uploadFixturesFiles(
          allFiles.map((f) => f.file),
          latestEtlStatus?.id ?? null,
        )
        .then((res) => {
          if (res.status === 201) {
            dispatch(setEtlFiles({ runId: res.data.run }))
            dispatch(setLatestEtlStatus())
            alertRef.current?.openAlert({
              message: t('file.upload.success'),
            })
            allFiles.forEach((f) => f.remove())
            dispatch(setLatestEtlStatus())
          }
        })
        .catch((err) => {
          if (err.status === 413) {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('file.upload.toobig.etl'),
            })
          } else {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('file.upload.error'),
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  return (
    <FsContainer>
      <Typography>{t('fixtures.prepare')}</Typography>
      <Dropzone
        onSubmit={handleSubmit}
        accept=".json"
        styles={{
          dropzone: {
            marginTop: 8,
            minHeight: 200,
            paddingLeft: 20,
            paddingRight: 20,
          },
          inputLabel: {
            padding: '16px 172px',
            color: theme.palette.primary.main,
            fontSize: 16,
            textAlign: 'center',
          },
          preview: {
            backgroundColor: fsColors.fsDarkBlue.colors[3],
            marginTop: 16,
            borderRadius: 4,
          },
          submitButton: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.background.paper,
            padding: '12px 24px',
          },
          inputLabelWithFiles: {
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.primary.main,
            padding: '8px 16px',
            marginLeft: '0px',
          },
        }}
        submitButtonContent={t('file.upload.submit')}
        inputWithFilesContent={t('add.more.files')}
        multiple
        inputContent={
          <Stack alignItems="center">
            <UploadIcon />
            <Typography variant="h6" textAlign="center" mt={2}>
              {t('file.drag.drop')}
            </Typography>
          </Stack>
        }
        onChangeStatus={(_, status) => {
          if (status === 'rejected_file_type') {
            alertRef.current?.openAlert({
              message: t('file.upload.toobig.etl'),
              severity: 'error',
            })
          } else {
            forceUpdate()
          }
        }}
      />
      <FsAlert ref={alertRef} />
    </FsContainer>
  )
}

export default FsFixtures
